import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, refreshSession, setAccessToken, setSessionLostHandler } from '../api/client';
import type { LoginResult, Me } from '../api/types';
import { outbox } from '../offline/outbox';

type State = { status: 'loading' } | { status: 'anon'; notice?: string } | { status: 'authed'; me: Me };

interface AuthApi {
  state: State;
  me: Me | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Reads the signed-in person and hotel details again (after the hotel details or logo are changed). */
  reloadMe: () => Promise<void>;
  can: (capability: string) => boolean;
}

const Ctx = createContext<AuthApi | null>(null);

/**
 * Signing out with no connection cannot tell the server, and the session cookie (which the page cannot touch) would
 * stay valid, so the next person to open the app on this device would be signed in as the last one. So the device
 * remembers that a sign-out is still owed, finishes it before any session is restored, and never restores a session
 * until it has.
 */
const OWED = 'aa-sign-out-owed';
const owed = {
  isSet: (): boolean => {
    try {
      return localStorage.getItem(OWED) === '1';
    } catch {
      return false;
    }
  },
  set: (): void => {
    try {
      localStorage.setItem(OWED, '1');
    } catch {
      /* private browsing: nothing to remember it in */
    }
  },
  clear: (): void => {
    try {
      localStorage.removeItem(OWED);
    } catch {
      /* nothing stored */
    }
  },
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<State>({ status: 'loading' });
  const qc = useQueryClient();

  const loadMe = useCallback(async () => {
    const me = await api<Me>('/me');
    void outbox.setOwner(me.id); // this person's saved changes are sent now; nobody else's ever are
    setState({ status: 'authed', me });
  }, []);

  // Restore the session from the httpOnly refresh cookie, if there is one.
  useEffect(() => {
    let alive = true;
    (async () => {
      if (owed.isSet()) {
        // finish the sign-out that could not reach the server; until it does, nobody is signed in on this device
        try {
          await api<void>('/auth/logout', { method: 'POST' });
          owed.clear();
        } catch {
          /* still no connection: stay signed out and try again next time */
        }
        if (alive) setState({ status: 'anon' });
        return;
      }
      if (await refreshSession()) {
        try {
          await loadMe();
          return;
        } catch {
          /* fall through to anon */
        }
      }
      if (alive) setState({ status: 'anon' });
    })();
    return () => {
      alive = false;
    };
  }, [loadMe]);

  useEffect(() => {
    setSessionLostHandler(() => {
      setAccessToken(null);
      qc.clear();
      void outbox.setOwner(null);
      setState({ status: 'anon', notice: 'You were signed out. Please sign in again.' });
    });
    return () => setSessionLostHandler(null);
  }, [qc]);

  const login = useCallback(
    async (email: string, password: string) => {
      const r = await api<LoginResult>('/auth/login', { method: 'POST', body: { email, password } });
      owed.clear(); // a new sign-in has a new session; the old one was replaced with it
      setAccessToken(r.accessToken);
      await loadMe();
    },
    [loadMe],
  );

  const logout = useCallback(async () => {
    try {
      await api<void>('/auth/logout', { method: 'POST' });
      owed.clear();
    } catch {
      owed.set(); // could not reach the server: it will be finished the next time the app starts with a connection
    } finally {
      setAccessToken(null);
      qc.clear();
      void outbox.setOwner(null);
      setState({ status: 'anon' });
    }
  }, [qc]);

  const value = useMemo<AuthApi>(() => {
    const me = state.status === 'authed' ? state.me : null;
    return { state, me, login, logout, reloadMe: loadMe, can: (c) => !!me && me.capabilities.includes(c) };
  }, [state, login, logout, loadMe]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthApi {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth must be used inside AuthProvider');
  return v;
}
